import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import { AppTheme } from "cityads-v3/types/app";
import { RootState } from "cityads-v3/types/reducer";
import { setTheme } from "cityads-v3/actions/app";

import { muiTheme } from 'cityads-v3/utils/styleUtils';
import darkTheme from 'cityads-v3/utils/muiTheme/darkTheme';

/**
 * Получение информации о текущей теме сайта
 * и функции для ее смены
 */
export function useThemeChanger() {
  const dispatch = useDispatch()
  const [cookie] = useCookies(['application_theme'])
  const browserTheme = useMemo(() => window?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light', [])
  const localStorageTheme = window?.localStorage?.getItem('theme')
  const storedTheme =
    localStorageTheme ?
      localStorageTheme === 'system'
        ? browserTheme
        : localStorageTheme
      : cookie?.application_theme

  const themeName = useSelector((state: RootState) => state.app.theme || storedTheme || browserTheme);

  const theme = useMemo(() => {
    switch (themeName) {
      case 'dark': return darkTheme;
      case 'light': return muiTheme;
      default: return muiTheme;
    }
  }, [themeName])

  const changeTheme = useCallback((theme: AppTheme, isSystem = false) => {
    dispatch(setTheme(theme))
    window.localStorage.setItem('theme', isSystem ? 'system' : theme)
  }, [dispatch])

  const toggleDark = useCallback(() => {
    const newTheme: AppTheme = themeName === 'dark' ? 'light' : 'dark';
    changeTheme(newTheme)
    return newTheme;
  }, [themeName, changeTheme])

  /** Используем системную тему как по-умолчанию */
  useEffect(() => {
    if (!window?.localStorage?.theme && !cookie?.application_theme) {
      window?.localStorage?.setItem('theme', 'system')
    }
  }, [cookie?.application_theme])

  return {
    themeName,
    theme,
    changeTheme,
    toggleDark,
  }
}

/**
 * Смена темы сайта при изменении системной темы
 */
export function useBrowserThemeListener() {
  const { changeTheme } = useThemeChanger()
  const browserTheme = useBrowserTheme()

  useEffect(() => {
    const isSystem = window?.localStorage?.getItem('theme') === 'system'
    if (isSystem) {
      changeTheme(browserTheme, isSystem)
    }
  }, [browserTheme, changeTheme])
}

/**
 * Получение текущего значения системной темы
 */
export function useBrowserTheme() {
  const matchMedia = useMemo(() => window?.matchMedia('(prefers-color-scheme: dark)'), [])
  const [theme, setTheme] = useState<'dark' | 'light'>(matchMedia?.matches ? 'dark' : 'light')

  useEffect(() => {
    const listener = (event: MediaQueryListEvent) => {
      const newColorScheme = event.matches ? "dark" : "light";
      setTheme(newColorScheme)
    }
    matchMedia.addEventListener?.('change', listener);

    return () => {
      matchMedia.removeEventListener?.('change', listener)
    }
  }, [matchMedia])

  return theme;
}
